import React, { useEffect, useState } from "react"

import { useTestData } from "@fuse/recoil/pluginStudio"
import {
  EventData,
  LiveData,
  LiveDataPanelProps,
  PluginConfigData,
  TestCaseUser,
  UserRole,
} from "@fuse/recoil/pluginStudio/types"

/**
 * Presentation component for the live data panel
 */
export const LiveDataPanelUI: React.FC<LiveDataPanelProps> = ({
  title = "Live Data",
  liveData,
  activeLiveUser,
  onToggleVisibility,
  onUserChange,
  onEventDataChange,
  onPluginConfigChange,
}) => {
  return (
    <div className="flex flex-col h-full">
      {/* Panel header */}
      <div className="px-3 py-2 bg-gray-800/80 border-b border-gray-700 flex items-center justify-between">
        <div className="flex items-center">
          <span className="font-medium text-sm text-white">{title}</span>
          {liveData.isDirty && liveData.sourceTestCaseId && (
            <span className="ml-2 text-xs px-1.5 py-0.5 bg-yellow-600 text-yellow-100 rounded-full">
              Out of Sync
            </span>
          )}
        </div>

        {/* Toggle visibility button */}
        {onToggleVisibility && (
          <button
            className="p-1 rounded hover:bg-gray-700 text-gray-400 hover:text-white"
            onClick={onToggleVisibility}
            title="Hide Live Data"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Panel content */}
      <div className="flex-1 overflow-auto p-4 bg-gray-800">
        <div className="grid grid-cols-2 gap-4 mb-4">
          {/* Event Data */}
          <div className="bg-gray-900 p-3 rounded-md border border-gray-700">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-medium text-gray-300">Event Data</h3>
              <span className="text-xs text-gray-500">Read-only</span>
            </div>
            <pre className="text-xs text-gray-400 overflow-auto max-h-32">
              {JSON.stringify(liveData.eventData, null, 2) || "{}"}
            </pre>
          </div>

          {/* Plugin Config */}
          <div className="bg-gray-900 p-3 rounded-md border border-gray-700">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-medium text-gray-300">
                Plugin Config
              </h3>
              <span className="text-xs text-gray-500">Read-only</span>
            </div>
            <pre className="text-xs text-gray-400 overflow-auto max-h-32">
              {JSON.stringify(liveData.pluginConfig, null, 2) || "{}"}
            </pre>
          </div>
        </div>

        <div className="mb-4">
          <div className="bg-gray-900 p-3 rounded-md border border-gray-700">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-medium text-gray-300">
                Event Plugin Data
              </h3>
              <span className="text-xs text-gray-500">
                Updated by plugin during runtime
              </span>
            </div>
            <pre className="text-xs text-gray-400 overflow-auto max-h-32">
              {JSON.stringify(liveData.eventPluginData, null, 2) || "{}"}
            </pre>
          </div>
        </div>

        <h3 className="text-sm font-medium text-gray-300 mt-4 mb-2">
          User Data
        </h3>
        <div className="grid grid-cols-2 gap-4">
          {liveData.users.map((user) => (
            <div
              key={user.id}
              className={`bg-gray-900 p-3 rounded-md border ${
                user.id === activeLiveUser?.id
                  ? "border-blue-500"
                  : "border-gray-700"
              }`}
              onClick={() => onUserChange && onUserChange(user.id)}
              style={{ cursor: onUserChange ? "pointer" : "default" }}
            >
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center">
                  <span
                    className={`w-2 h-2 rounded-full mr-2 ${
                      user.role === UserRole.HOST_EVENT_VIEW
                        ? "bg-blue-400"
                        : user.role === UserRole.HOST_CONFIG_VIEW
                          ? "bg-purple-400"
                          : "bg-green-400"
                    }`}
                  ></span>
                  <h4 className="text-sm font-medium text-gray-300">
                    {user.name} ({user.role})
                    {user.viewAs && user.viewAs !== user.role && (
                      <span className="text-xs ml-1 text-gray-400">
                        (viewing as {user.viewAs})
                      </span>
                    )}
                  </h4>
                </div>
                {user.id === activeLiveUser?.id && (
                  <span className="text-xs px-1.5 py-0.5 bg-blue-600 text-blue-100 rounded">
                    Active
                  </span>
                )}
              </div>

              <div className="mb-2">
                <div className="text-xs text-gray-500 mb-1">User Data</div>
                <pre className="text-xs text-gray-400 overflow-auto max-h-24">
                  {JSON.stringify(
                    user.userData || user.userPluginData || {},
                    null,
                    2,
                  )}
                </pre>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Container component that connects to Recoil state
 */
const LiveDataPanel: React.FC<{
  title?: string
  onToggleVisibility?: () => void
}> = ({ title, onToggleVisibility }) => {
  // Connect to Recoil state
  const {
    liveData,
    activeLiveUser,
    updateLiveEventData,
    updateLivePluginConfig,
    setLiveActiveUser,
  } = useTestData()

  // Handle user selection
  const handleUserChange = (userId: string) => {
    setLiveActiveUser(userId)
  }

  // Handle event data changes
  const handleEventDataChange = (data: EventData) => {
    updateLiveEventData(data)
  }

  // Handle plugin config changes
  const handlePluginConfigChange = (config: PluginConfigData) => {
    updateLivePluginConfig(config)
  }

  return (
    <LiveDataPanelUI
      title={title}
      liveData={liveData}
      activeLiveUser={activeLiveUser}
      onToggleVisibility={onToggleVisibility}
      onUserChange={handleUserChange}
      onEventDataChange={handleEventDataChange}
      onPluginConfigChange={handlePluginConfigChange}
    />
  )
}

export default LiveDataPanel
